import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Stack,
  Box,
  useTheme,
  alpha,
  IconButton,
  Fade,
  useMediaQuery,
} from '@mui/material';
import {
  Work,
  Code,
  Psychology,
  ArrowForward,
  Schedule,
  Group,
  PlayArrow,
  Terminal,
} from '@mui/icons-material';

function FeatureSummary({ feature, onClick }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isHovered, setIsHovered] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Enterprise':
        return <Work sx={{ fontSize: 20 }} />;
      case 'Independent':
        return <Code sx={{ fontSize: 20 }} />;
      case 'Prototype':
        return <Psychology sx={{ fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Enterprise':
        return '#00ffff';
      case 'Independent':
        return '#ff6b35';
      case 'Prototype':
        return '#ffab00';
      default:
        return theme.palette.grey[500];
    }
  };

  const categoryColor = getCategoryColor(feature.category);

  const handleTouchStart = () => {
    if (isMobile) {
      setIsTouched(true);
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      setTimeout(() => setIsTouched(false), 150);
    }
  };

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        height: '100%',
        minHeight: { xs: 320, sm: 380, md: 400 },
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        background: `
          linear-gradient(145deg, #0a0a0a, #111111),
          radial-gradient(circle at top right, ${alpha(categoryColor, 0.1)} 0%, transparent 50%)
        `,
        border: `1px solid ${alpha(categoryColor, 0.3)}`,
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        transform: isTouched ? 'scale(0.98)' : 'scale(1)',
        '&:hover': {
          transform: isMobile ? 'scale(1)' : 'translateY(-12px) scale(1.02)',
          border: `1px solid ${categoryColor}`,
          boxShadow: `
            0 25px 50px ${alpha(categoryColor, 0.4)},
            inset 0 1px 0 ${alpha(categoryColor, 0.2)},
            0 0 30px ${alpha(categoryColor, 0.3)}
          `,
          '& .terminal-header': {
            background: `linear-gradient(90deg, ${categoryColor}, ${alpha(categoryColor, 0.7)})`,
          },
          '& .feature-icon': {
            transform: isMobile ? 'scale(1.05)' : 'scale(1.1)',
            color: categoryColor,
            textShadow: `0 0 15px ${categoryColor}`,
            filter: `drop-shadow(0 0 15px ${categoryColor}) brightness(1.1)`,
          },
          '& .view-details': {
            opacity: 1,
            transform: 'translateX(0) scale(1)',
          },
          '& .terminal-prompt': {
            color: categoryColor,
          },
          '& .code-line': {
            background: `linear-gradient(90deg, transparent, ${alpha(categoryColor, 0.1)}, transparent)`,
          },
        },
        // Mobile touch states
        '&:active': isMobile ? {
          transform: 'scale(0.95)',
        } : {},
      }}
      onClick={onClick}
    >
      {/* Terminal Header */}
      <Box
        className="terminal-header"
        sx={{
          height: 30,
          background: `linear-gradient(90deg, ${alpha(categoryColor, 0.3)}, ${alpha(categoryColor, 0.1)})`,
          borderBottom: `1px solid ${alpha(categoryColor, 0.3)}`,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          position: 'relative',
          transition: 'all 0.3s ease',
        }}
      >
        <Stack direction="row" spacing={0.5}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff5f56' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#27ca3f' }} />
        </Stack>
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            color: alpha(categoryColor, 0.8),
            fontFamily: '"Fira Code", monospace',
            fontSize: { xs: '0.6rem', sm: '0.65rem' },
          }}
        >
          {feature.category.toLowerCase()}_project.sh
        </Typography>
      </Box>

      {/* Terminal Content Area */}
      <Box sx={{ position: 'relative', overflow: 'hidden', flexGrow: 1 }}>
        <CardMedia
          component="div"
          sx={{
            height: { xs: 140, sm: 180 },
            pt: { xs: 2, sm: 2.5 }, // Add top padding for spacing
            pb: { xs: 2, sm: 2.5 }, // Add bottom padding for balanced spacing
            background: '#000000', // Simple black background
            // Alternative options (uncomment to try):
            // background: `linear-gradient(135deg, #000000 0%, #0a0a0a 100%)`, // Subtle dark gradient
            // background: `
            //   linear-gradient(135deg, #000000 0%, #0a0a0a 100%),
            //   repeating-linear-gradient(
            //     0deg,
            //     transparent,
            //     transparent 2px,
            //     ${alpha('#00ff41', 0.02)} 2px,
            //     ${alpha('#00ff41', 0.02)} 3px
            //   )
            // `, // Terminal scan lines effect
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'all 0.3s ease',
          }}
        >
          {/* Video Indicator */}
          {feature.video && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                background: `linear-gradient(145deg, ${alpha('#ff6b35', 0.9)}, ${alpha('#ff6b35', 0.7)})`,
                borderRadius: '50%',
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${alpha('#fff', 0.3)}`,
                boxShadow: `0 4px 12px ${alpha('#ff6b35', 0.4)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: `0 6px 16px ${alpha('#ff6b35', 0.6)}`,
                },
              }}
            >
              <PlayArrow
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                  color: '#fff',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                }}
              />
            </Box>
          )}

          {/* Animated Background Lines */}
          <Box
            className="code-line"
            sx={{
              position: 'absolute',
              top: '20%',
              left: 0,
              right: 0,
              height: '1px',
              background: 'transparent',
              transition: 'all 0.3s ease',
            }}
          />
          <Box
            className="code-line"
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: 0,
              right: 0,
              height: '1px',
              background: 'transparent',
              transition: 'all 0.3s ease',
            }}
          />

          {feature.image ? (
            <Box
              component="img"
              src={feature.image}
              alt={feature.name}
              className="feature-icon"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transformStyle: 'preserve-3d',
                filter: `drop-shadow(0 0 10px ${alpha(categoryColor, 0.3)})`,
              }}
            />
          ) : (
            <Box
              className="feature-icon"
              sx={{
                fontSize: { xs: 50, sm: 60 },
                color: alpha(categoryColor, 0.6),
                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transformStyle: 'preserve-3d',
              }}
            >
              {getCategoryIcon(feature.category)}
            </Box>
          )}
        </CardMedia>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 2,
            color: '#00ff41',
            fontFamily: '"Fira Code", monospace',
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.3,
          }}
        >
          {feature.name}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitLineClamp: { xs: 3, sm: 2 },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
          }}
        >
          {feature.summary}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {feature.tags.slice(0, isMobile ? 2 : 3).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                height: { xs: 20, sm: 22 },
                background: `linear-gradient(145deg, ${alpha(categoryColor, 0.1)}, transparent)`,
                borderColor: alpha(categoryColor, 0.3),
                color: alpha(categoryColor, 0.9),
                fontFamily: '"Fira Code", monospace',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: `linear-gradient(145deg, ${alpha(categoryColor, 0.2)}, ${alpha(categoryColor, 0.1)})`,
                  borderColor: alpha(categoryColor, 0.6),
                  transform: isMobile ? 'none' : 'scale(1.05)',
                },
              }}
            />
          ))}
          {feature.tags.length > (isMobile ? 2 : 3) && (
            <Chip
              label={`+${feature.tags.length - (isMobile ? 2 : 3)}`}
              size="small"
              variant="filled"
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                height: { xs: 20, sm: 22 },
                background: `linear-gradient(145deg, ${alpha('#666', 0.3)}, ${alpha('#666', 0.1)})`,
                color: '#999',
                fontFamily: '"Fira Code", monospace',
                border: `1px solid ${alpha('#666', 0.3)}`,
              }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default FeatureSummary; 